import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { API, authHeaders, useAdminAuthError } from "@/lib/adminApi";
import { PROVIDER_LABELS } from "@/lib/adminUtils";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import Spinner from "@/components/admin/Spinner";
import CheckoutLinkResult from "@/components/admin/CheckoutLinkResult";

const STATUS_LABELS = {
  completed: { label: "Complété", color: "emerald" },
  pending:   { label: "En attente", color: "amber" },
  failed:    { label: "Échoué", color: "red" },
};

export const PaymentsPage = () => {
  const { year } = useOutletContext();
  const handleAuthError = useAdminAuthError();

  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [generatingFor, setGeneratingFor] = useState(null);
  const [checkoutLink, setCheckoutLink] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentsRes, membersRes] = await Promise.all([
        axios.get(`${API}/payments/admin-list?year=${year}`, { headers: authHeaders() }),
        axios.get(`${API}/members/`, { headers: authHeaders() }),
      ]);
      setPayments(paymentsRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Erreur lors du chargement des paiements.");
    } finally {
      setLoading(false);
    }
  }, [year, handleAuthError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const memberById = members.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});

  const memberName = (p) => {
    const m = memberById[p.member_id];
    return m ? `${m.first_name || ""} ${m.last_name || ""}`.trim() || "—" : "—";
  };

  const providers = [...new Set(payments.map((p) => p.provider).filter(Boolean))];

  const filteredPayments = payments.filter((p) => {
    const matchSearch = memberName(p).toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || p.status === statusFilter;
    const matchProvider = providerFilter === "all" || p.provider === providerFilter;
    return matchSearch && matchStatus && matchProvider;
  });

  const generateLink = async (memberId, name, { regenerate = false } = {}) => {
    const res = await axios.post(
      `${API}/payments/stripe/generate-checkout-link`,
      { member_id: memberId, regenerate },
      { headers: authHeaders() }
    );
    setCheckoutLink({
      url: res.data.link_url,
      expires_at: res.data.expires_at,
      amount: res.data.amount,
      member_id: memberId,
      member_name: name,
    });
  };

  const handleGenerateLink = async (p) => {
    setGeneratingFor(p.id);
    try {
      await generateLink(p.member_id, memberName(p));
    } catch (err) {
      if (!handleAuthError(err)) {
        toast.error(err.response?.data?.detail || "Erreur lors de la génération du lien.");
      }
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleRegenerate = async () => {
    if (!checkoutLink) return;
    setRegenerating(true);
    try {
      await generateLink(checkoutLink.member_id, checkoutLink.member_name, { regenerate: true });
      toast.success("Nouveau lien généré — l'ancien n'est plus valable.");
    } catch (err) {
      if (!handleAuthError(err)) {
        toast.error(err.response?.data?.detail || "Erreur lors de la régénération du lien.");
      }
    } finally {
      setRegenerating(false);
    }
  };

  const totalCompleted = filteredPayments
    .filter((p) => p.status === "completed")
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return (
    <section className="fade-up fade-up-1">
      <PageHeader
        title="Paiements"
        subtitle={`${filteredPayments.length} paiement${filteredPayments.length !== 1 ? "s" : ""} · ${totalCompleted.toLocaleString("fr-FR")} € encaissés · exercice ${year}`}
        actions={
          <>
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher un membre…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 w-48 transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="all">Tous statuts</option>
              <option value="completed">Complété</option>
              <option value="pending">En attente</option>
              <option value="failed">Échoué</option>
            </select>
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <option value="all">Tous modes</option>
              {providers.map((p) => (
                <option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>
              ))}
            </select>
          </>
        }
      />

      {/* Lien de paiement généré */}
      {checkoutLink && (
        <div className="mb-4">
          <CheckoutLinkResult
            memberName={checkoutLink.member_name}
            url={checkoutLink.url}
            amount={checkoutLink.amount}
            expiresAt={checkoutLink.expires_at}
            onClose={() => setCheckoutLink(null)}
            onRegenerate={handleRegenerate}
            regenerating={regenerating}
          />
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32"><Spinner /></div>
        ) : filteredPayments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
            <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <p>Aucun paiement trouvé.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-[10px] text-slate-400 text-right px-4 pt-2 pb-0 sm:hidden">← Faites défiler →</p>
            <table className="text-sm" style={{ minWidth: "720px", width: "100%" }}>
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  {["Date", "Membre", "Montant", "Mode", "Type", "Statut", ""].map((label, i) => (
                    <th
                      key={i}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((p, i) => {
                  const s = STATUS_LABELS[p.status] || { label: p.status, color: "slate" };
                  return (
                    <tr
                      key={p.id}
                      className={`hover:bg-slate-50/40 transition-colors ${i < filteredPayments.length - 1 ? "border-b border-slate-50" : ""}`}
                    >
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR") : "—"}
                      </td>
                      <td className="px-4 py-3.5 font-semibold text-slate-900 whitespace-nowrap">{memberName(p)}</td>
                      <td className="px-4 py-3.5 text-slate-700 font-medium whitespace-nowrap">
                        {p.amount != null ? `${Number(p.amount).toLocaleString("fr-FR")} €` : "—"}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">
                        {PROVIDER_LABELS[p.provider] || p.provider || "—"}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap capitalize">
                        {p.payment_method_type === "sepa_debit" ? "Prélèvement" : p.payment_method_type === "card" ? "Carte" : p.membership_type || "—"}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <StatusBadge color={s.color}>{s.label}</StatusBadge>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right">
                        {p.status === "pending" && p.member_id && (
                          <button
                            onClick={() => handleGenerateLink(p)}
                            disabled={generatingFor === p.id}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all hover:bg-slate-50 disabled:opacity-50"
                            style={{ borderColor: "#635BFF", color: "#635BFF" }}
                          >
                            {generatingFor === p.id ? "…" : "Lien de paiement →"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default PaymentsPage;
